import { useTranslation } from 'react-i18next';

import { DownOutlined, UpOutlined } from '@ant-design/icons';

import * as St from './NavBarPricing.styles';

import { MinimumPurchaseNotification } from '~/UI/components/molecules/MinimumPurchaseNotification/MinimumPurchaseNotification';
import { useOrderInvoice } from '~/UI/pages/OrderSummaryPage/hooks';
import { IMAGE_SIZE } from '~/core/constants/constants';
import { FocusElement, StepName } from '~/core/enums';
import useEstimateSaver from '~/core/hooks/useEstimateSaver';
import { useSmallJobs } from '~/core/hooks/useSmallJobs';
import { imgUrl } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectCurrentEstimateCode } from '~/redux/slices/estimate';
import {
  resetFocusElement,
  selectFocusElement,
  toggleFocusElement,
} from '~/redux/slices/layout';
import {
  checkEnabledStepsByListener,
  goToStep,
  selectSteps,
} from '~/redux/slices/stepper';
import CommonButton from '~atoms/CommonButton';
import OrderInvoice from '~organisms/OrderInvoice';

const selectEnabledStepsByListener = checkEnabledStepsByListener([
  StepName.designEdge,
  StepName.designCornerRadius,
  StepName.orderSummary,
]);

const NavBarPricing = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const focusElement = useAppSelector(selectFocusElement);
  const steps = useAppSelector(selectSteps);
  const [isEdgeStepEnabled, isCornerStepEnabled, isOrderStepEnabled] =
    useAppSelector(selectEnabledStepsByListener);
  const selectedColor = useAppSelector(selectSelectedColor);

  const isPriceDropdownOpen = focusElement === FocusElement.ORDER_SUMMARY;

  const currentEstimateCode = useAppSelector(selectCurrentEstimateCode);
  const { isDesignDirty } = useEstimateSaver();
  const { useMinQtyAreaForPricing, smallJobMinimumArea } = useSmallJobs();
  const { displayTotal } = useOrderInvoice();

  const shouldViewEstimateDetails = !!currentEstimateCode && !isDesignDirty;

  const fallbackBeautyShotImageUrl = '/public/assets/images/EstimateListBG.jpg';
  const colorImage =
    selectedColor?.beautyShotImageUrl ?? selectedColor?.swatchImageUrl;
  const beautyShotImageUrl: string = colorImage
    ? imgUrl(colorImage, IMAGE_SIZE.LARGE)
    : fallbackBeautyShotImageUrl;

  const goToPage = (stepName: StepName.thankYou | StepName.orderSummary) => () => {
    dispatch(resetFocusElement());
    dispatch(goToStep(stepName));
  };

  return (
    <>
      <St.Container
        onClick={() => dispatch(toggleFocusElement(FocusElement.ORDER_SUMMARY))}
        data-is-open={isPriceDropdownOpen}
      >
        <span>{t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.TOTAL')}</span>
        <St.TotalToPayContainer>
          <strong>{displayTotal}</strong>
          <DownOutlined />
        </St.TotalToPayContainer>
      </St.Container>

      {isPriceDropdownOpen && (
        <St.DropdownContainer onClick={() => dispatch(resetFocusElement())}>
          <St.DropdownCard onClick={(e) => e.stopPropagation()}>
            <St.DropdownImageContainer
              style={{
                backgroundImage: [
                  `url("${beautyShotImageUrl}")`,
                  `url("${fallbackBeautyShotImageUrl}")`,
                ].join(', '),
              }}
            />
            <St.DropdownInvoiceContainer>
              <OrderInvoice tableMaxHeight="380rem" />

              <St.NotificationContainer>
                <MinimumPurchaseNotification
                  useMinQtyAreaForPricing={useMinQtyAreaForPricing}
                  smallJobMinimumArea={smallJobMinimumArea}
                />
              </St.NotificationContainer>

              <St.DropdownButtonContainer>
                <CommonButton
                  disabled={!isOrderStepEnabled}
                  onClick={shouldViewEstimateDetails
                    ? goToPage(StepName.thankYou)
                    : goToPage(StepName.orderSummary)
                  }
                  style={{ fontSize: '20rem' }}
                >
                  {shouldViewEstimateDetails
                    ? t('BUTTON.VIEW_ESTIMATE_DETAILS')
                    : t('BUTTON.CHECKOUT')
                  }
                </CommonButton>
                {!isOrderStepEnabled && (
                  <St.ErrorContainer>
                    {steps.includes(StepName.designEdge) && !isEdgeStepEnabled
                      ? t('VALIDATION_MESSAGE.REQUIRE_SHAPE')
                      : (steps.includes(StepName.designCornerRadius) &&
                            !isCornerStepEnabled) ||
                          !steps.includes(StepName.designCornerRadius)
                        ? t('VALIDATION_MESSAGE.REQUIRE_EDGE')
                        : t('VALIDATION_MESSAGE.REQUIRE_CORNER')}
                  </St.ErrorContainer>
                )}
              </St.DropdownButtonContainer>
            </St.DropdownInvoiceContainer>
          </St.DropdownCard>
          <St.CloseContainer>
            <UpOutlined />
            &nbsp;
            <span>Close</span>
          </St.CloseContainer>
        </St.DropdownContainer>
      )}
    </>
  );
};

export default NavBarPricing;
